import React from 'react';
import './MainPage.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import UserCard from 'containers/UserCard';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { asyncFetchUser } from 'store/slices/thunks';

const SCANNER_ELEMENT_ID = 'qr-scanner';

const selectUserStore = (state: RootState): RootState['userStore'] =>
    state.userStore;

function detectMobile(forceMobile: boolean): boolean {
    if (forceMobile) return true;
    if (typeof navigator !== 'undefined') {
        if (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            )
        ) {
            return true;
        }
    }
    return typeof window !== 'undefined' && window.innerWidth <= 768;
}

function MainPage(): React.ReactElement {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [scanning, setScanning] = React.useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const scannerRef = React.useRef<Html5Qrcode | null>(null);

    const [searchParams] = useSearchParams();
    const isMobile = React.useMemo(
        () => detectMobile(searchParams.get('mobile') === '1'),
        [searchParams]
    );

    const onLoadComplete = (): void => {
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const onError = (err?: { detail: string }): void => {
        setError(err?.detail || 'Неизвестная ошибка');
    };

    const onLoad = (): void => {
        setLoading(true);
        setError(null);
    };

    const fetchByCode = React.useCallback(
        (rawCode: string): void => {
            const vkId = rawCode.replace('--', 'golden-like-');
            if (!vkId) {
                setError('Неверный формат билета');
                return;
            }
            onLoad();
            dispatch(asyncFetchUser(vkId))
                .unwrap()
                .catch(onError)
                .finally(onLoadComplete);
        },
        [dispatch]
    );

    const handleEnter = React.useCallback((): void => {
        const input = inputRef.current;
        if (!input) return;
        fetchByCode(input.value);
        input.value = '';
    }, [fetchByCode]);

    const stopScanner = React.useCallback(async (): Promise<void> => {
        const scanner = scannerRef.current;
        if (!scanner) return;
        try {
            await scanner.stop();
            await scanner.clear();
        } catch {
            // ignore
        }
        scannerRef.current = null;
        setScanning(false);
    }, []);

    const onScanSuccess = React.useCallback(
        (code: string): void => {
            stopScanner().catch(() => undefined);
            fetchByCode(code);
        },
        [stopScanner, fetchByCode]
    );

    const onScanFrameError = React.useCallback((): void => {
        // per-frame decode errors — игнорируем
    }, []);

    const startScanner = React.useCallback(async (): Promise<void> => {
        setError(null);
        setScanning(true);
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
        scannerRef.current = scanner;
        try {
            await scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 280, height: 160 } },
                onScanSuccess,
                onScanFrameError
            );
        } catch (e: unknown) {
            setError((e as Error)?.message ?? 'Не удалось включить камеру');
            await stopScanner();
        }
    }, [stopScanner, onScanSuccess, onScanFrameError]);

    React.useEffect(
        () => () => {
            stopScanner().catch(() => undefined);
        },
        [stopScanner]
    );

    const navigate = useNavigate();
    React.useEffect(() => {
        if (!localStorage.token) navigate('/login');
    }, [navigate]);

    const { user } = useAppSelector(selectUserStore);

    return (
        <div className="main-page">
            {isMobile ? (
                <>
                    {!scanning && (
                        <button
                            type="button"
                            className="scan-button"
                            onClick={startScanner}
                        >
                            Сканировать
                        </button>
                    )}
                    <div
                        id={SCANNER_ELEMENT_ID}
                        style={{ width: '100%', maxWidth: 480 }}
                    />
                    {scanning && (
                        <button
                            type="button"
                            className="scan-button scan-button--stop"
                            onClick={stopScanner}
                        >
                            Остановить
                        </button>
                    )}
                </>
            ) : (
                <input
                    ref={inputRef}
                    disabled={loading}
                    type="text"
                    autoFocus
                    onKeyDown={(e): void => {
                        if (e.key === 'Enter') handleEnter();
                    }}
                />
            )}
            {!!error && <div className="error">{error}</div>}
            {loading && <div className="loader" />}
            {user && !loading && <UserCard user={user} />}
        </div>
    );
}

export default React.memo(MainPage);
